/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {Injectable} from '@angular/core';
import {Attendance, AttendanceChannel} from '../../interfaces/attendance';
import {AttendanceStatus} from '../../interfaces/attendance-status.enum';
import {InputStateVisibility} from '../../interfaces/input-state-visibility.enum';
import {UserService} from 'src/app/core/services/user.service';
import {isAfter24Hours} from '../../helpers/is-after-24-hours.helper';
import {ChannelUtilsService} from '../../services/channel-utils.service';
import { EMPTY, Observable, ReplaySubject, Subject, distinctUntilChanged, finalize, map, of, shareReplay, skip, switchMap, tap } from 'rxjs';
import { AttendanceService } from '../../services/attendance.service';
import { ChannelActionButtons, InputStateData, InputVisibility } from '../../interfaces/input-state-data.interface';
import { CompanyService } from '@core/services/company.service';
import { FilterService } from '../../services/filter.service';
import { Company } from '@core/interfaces/company';
import { LiveChatStateService } from '../../services/live-chat-state.service';
import { StartService } from '@core/services/start.service';
import { WHATSAPP_WEB_USER_REFERENCE_ID } from '../../constants/attendant.constant';
import { CREDITS_PER_WHATSAPP_MESSAGE, CreditsStatusService } from '../../services/credits-status.service';
import { FeatureToggleService } from '@core/services/feature-toggle.service';
import { FeatureToggles } from '@core/enums/feature-toggles.enum';
import { resolveCreditsCompanyId } from '../../helpers/resolve-credits-company-id.helper';
import { isBsuid } from '../../helpers/is-bsuid.helper';
import { isFepFreeMessage } from '../../helpers/free-entry-point-state.helper';

const WHATSAPP_GUPSHUP_PLATFORM = 'whatsapp_gupshup';


@Injectable({providedIn:'root'})
export class ChatInputVisibilityService {
  constructor(
    private userService: UserService,
    private channelUtils: ChannelUtilsService,
    private attendanceSevice: AttendanceService,
    private company: CompanyService,
    private filterService: FilterService,
    private liveChatStateService: LiveChatStateService,
    private startService: StartService,
    private creditsStatusService: CreditsStatusService,
    private featureToggleService: FeatureToggleService,
  ) {}
  public inputState?:InputVisibility;
  public closeEmojiPicker$ = new Subject<void>();
  public readonly refreshInputState$ = (this.liveChatStateService.blockContactFeatureEnabled$ ?? EMPTY).pipe(
    skip(1),
    distinctUntilChanged()
  );
  private requestInProgress$: ReplaySubject<Observable<InputVisibility>> | null = null;
  private oldAttendenceId?: string
  
  public determineTextInputState$(
    attendance: Attendance,
  ): Observable<InputVisibility> {
    if(this.oldAttendenceId === attendance.id){
      this.oldAttendenceId = attendance.id
      if (this.requestInProgress$) {
        return this.requestInProgress$.pipe(switchMap((obs) => obs));
      }
    }
      this.requestInProgress$ = new ReplaySubject(1);
      // Global users' company list lives in memory only and is only reliably
      // populated once /start/livechat responds — wait for it so channel
      // action buttons (email/whatsapp/phone) aren't computed against an
      // empty company list. Non-global users are unaffected (immediate).
      const source$ = this.userService.isCurrentUserGlobal()
        ? this.startService.sessionSettled$.pipe(
            switchMap(() => this._determineTextInputState$(attendance))
          )
        : this._determineTextInputState$(attendance);
      const request$ = source$.pipe(
        tap((inputState) => {
          this.inputState = inputState;
        }),
        finalize(() => {
          this.requestInProgress$ = null;
        }),
        shareReplay(1)
      );
      this.oldAttendenceId = attendance.id
      this.requestInProgress$.next(request$);
      this.requestInProgress$.complete();
  
      return request$;
  }

  private _determineTextInputState$(
    attendance: Attendance
  ): Observable<InputVisibility> {
    if (this.isContactBlocked(attendance)) {
      return of({ inputState: InputStateVisibility.LOCKED_CONTACT_BLOCKED });
    }

    if (this.isCreditsExhausted(attendance)) {
      return of({ inputState: InputStateVisibility.LOCKED_CREDITS_EXHAUSTED });
    }

    // Without this, opening a WhatsApp attendance resolves the input as
    // UNLOCKED immediately (isCreditsExhausted reads a stale/mismatched
    // status synchronously, before the GET /company/credits fired by
    // refreshCreditsStatusIfNeeded has a chance to resolve), letting the
    // agent send on a company that later turns out to be exhausted — and
    // stays that way forever if the request fails, since status.isExhausted
    // never flips to true. Lock (reusing the loading CTA) until this
    // specific company's status is confirmed one way or the other.
    if (this.isCreditsStatusPending(attendance)) {
      return of({ inputState: InputStateVisibility.LOADING_CTA });
    }

    if(this.channelUtils.isInstagramChannel(attendance.currentContactChannel.channel)){
      return this.getInstagramCta(attendance);
    }
    if (this.channelUtils.isBookingChannel(attendance.currentContactChannel.channel))
      return this._determineBookingTextInputState$(attendance);

    if (this.channelUtils.isExpediaChannel(attendance.currentContactChannel.channel))
      return this._determineBookingTextInputState$(attendance);

    if (this.channelUtils.isTiktokChannel(attendance.currentContactChannel.channel))
      return this._determineBookingTextInputState$(attendance);

    if (attendance.status === AttendanceStatus.ARCHIVED) 
      return this.getArchivedAttendanceCtaVisibility(attendance);

    if (attendance.currentContactChannel.channel === AttendanceChannel.PHONE) 
      return this.getPhoneCta(attendance)

    if (attendance.currentContactChannel.channel === AttendanceChannel.EMAIL)
      return this.getLockedEmailAttendanceCtaVisibility(attendance)

    return this.getCommonInputValidations(attendance);
  }

  private isContactBlocked(attendance: Attendance): boolean {
    return this.liveChatStateService.blockContactFeatureEnabled
      && this.liveChatStateService.currentAttendance?.isContactBlocked === true;
  }

  // Only WhatsApp is billed via the credits wallet today — other channels
  // must stay unaffected by an exhausted balance. FEP takes priority: those
  // messages aren't charged, so an empty wallet shouldn't block sending them.
  // Gated by the same toggle as the rest of the message-cost feature, so
  // turning it off fully reverts to the old (never-blocked) behavior.
  //
  // The companyId check guards against an out-of-order response: credits
  // requests aren't guaranteed to resolve in the order they were fired (see
  // ChatInputComponent.fetchCredits), so `status` can momentarily still
  // hold a previous, different company's (possibly exhausted) result. Never
  // lock an attendance based on another company's balance.
  private isCreditsExhausted(attendance: Attendance): boolean {
    return attendance.currentContactChannel.channel === AttendanceChannel.WHATSAPP
      && this.featureToggleService.companyCanAccessFromCache(
        FeatureToggles.ENABLED_LIVECHAT_SHOW_MESSAGE_COST
      )
      && this.creditsStatusService.status.isExhausted
      && this.creditsStatusService.status.companyId === this.resolveCreditsCompanyId(attendance)
      && !isFepFreeMessage(attendance.stats)
      && !this.isPaidByMetaBonus(attendance);
  }

  // Credits v2: inside the 24h window a reply is a service message, which the
  // Meta bonus pays for, so an empty balance with bonus left must not lock it
  // (generic-bot also lets the send through: it only blocks when neither the
  // balance nor the bonus covers one message). With the window closed only a
  // template can be sent and the bonus never pays templates, so it stays locked.
  private isPaidByMetaBonus(attendance: Attendance): boolean {
    return this.featureToggleService.companyCanAccessFromCache(FeatureToggles.ENABLE_WHATSAPP_CREDITS_V2)
      && (this.creditsStatusService.status.credits?.bonusBalance ?? 0) >= CREDITS_PER_WHATSAPP_MESSAGE
      && !this.checkIsMetaAndUnavailable(attendance);
  }

  // Only true while a request for THIS company is in flight or just failed
  // (status.companyId matches) — a plain mismatch is not enough, since that's
  // also the case for a company nobody has fetched credits for yet (e.g. an
  // attendance not assigned to the current agent, see
  // ChatInputComponent.isAttendanceAssignedToMe), which must stay unlocked.
  private isCreditsStatusPending(attendance: Attendance): boolean {
    if (attendance.currentContactChannel.channel !== AttendanceChannel.WHATSAPP) {
      return false;
    }
    if (
      !this.featureToggleService.companyCanAccessFromCache(
        FeatureToggles.ENABLED_LIVECHAT_SHOW_MESSAGE_COST
      )
    ) {
      return false;
    }
    if (isFepFreeMessage(attendance.stats)) {
      return false;
    }
    const status = this.creditsStatusService.status;
    return (
      status.companyId === this.resolveCreditsCompanyId(attendance) &&
      (status.isLoading || status.hasError)
    );
  }

  // Credits are billed to the company that owns the WhatsApp number, which
  // may differ from currentCompany after the bot navigates between companies.
  private resolveCreditsCompanyId(attendance: Attendance): string | undefined {
    return resolveCreditsCompanyId(attendance, this.company.getCompanies());
  }

  private getInstagramCta(attendance: Attendance): Observable<InputVisibility> {
    const hasInstagramIntegrityIssue = attendance.currentCompany?.instagramIntegrity === false;

    if (hasInstagramIntegrityIssue) {
      return this.hasOtherAvailableChannel(attendance).pipe(
        map((hasAvailable) => ({
          inputState: InputStateVisibility.LOCKED_INSTAGRAM_INTEGRITY,
          data: {
            ...this.determineChannelActionButtons(attendance),
            hasOtherContactChannel: hasAvailable,
          },
        }))
      );
    }

    return this.getCommonInputValidations(attendance);
  }

  private getCommonInputValidations(attendance: Attendance): Observable<InputVisibility> {
    if (attendance.status === AttendanceStatus.ARCHIVED) 
      return this.getArchivedAttendanceCtaVisibility(attendance);

    if(this.checkIsMetaAndUnavailable(attendance))
      return this.getLockedCtaVisibility(attendance);

    if (attendance.status === AttendanceStatus.CLOSED)
      return of({inputState: InputStateVisibility.LOCKED_ATTENDANCE_STOPPED});

    if (this.checkIfOtherAgentAssigned(attendance)) 
      return of({inputState: InputStateVisibility.LOCKED_OTHER_AGENT_ASSIGNED});

    return of({inputState: InputStateVisibility.UNLOCKED});
  }

  private _determineBookingTextInputState$(attendance: Attendance): Observable<InputVisibility> {
    if (attendance.status === AttendanceStatus.ARCHIVED) {
      return this.getArchivedAttendanceCtaVisibility(attendance).pipe(
        map((archivedVisibility) => {
          const { channel } = attendance.currentContactChannel;
          const isBooking = this.channelUtils.isBookingChannel(channel);
          const isExpedia = this.channelUtils.isExpediaChannel(channel);
          const isTiktok = this.channelUtils.isTiktokChannel(channel);
      
          if (archivedVisibility.inputState !== InputStateVisibility.HAS_NO_OTHER_CONTACT_CHANNEL) {
            return archivedVisibility;
          }
      
          if (isBooking) {
            return this.getBookingCtaVisibility(attendance);
          }
      
          if (isExpedia) {
            return this.getExpediaCtaVisibility(attendance);
          }

          if (isTiktok) {
            return this.getTiktokCtaVisibility(attendance);
          }
      
          return archivedVisibility;
        })
      );
    }

    const isWithOtherAgent = this.checkIfOtherAgentAssigned(attendance);
    if (attendance.status === AttendanceStatus.OPENED  && isWithOtherAgent) {
      return of({ inputState: InputStateVisibility.LOCKED_OTHER_AGENT_ASSIGNED });
    }

    if (attendance.status === AttendanceStatus.CLOSED)
      return of({inputState: InputStateVisibility.LOCKED_ATTENDANCE_STOPPED});

    return of({ inputState: InputStateVisibility.UNLOCKED });
  }

  private getPhoneCta(attendance: Attendance) {
    return this.hasOtherAvailableChannel(attendance).pipe(
      map((hasAvailable) => {
        const cta = {
          data: {
            ...this.determineChannelActionButtons(attendance),
            hasOtherContactChannel: hasAvailable,
          },
          inputState: InputStateVisibility.IS_CHANNEL_PHONE,
        };
        return cta;
      })
    );
  }
  

  private getLockedCtaVisibility(attendance: Attendance){
    return this.hasOtherAvailableChannel(attendance).pipe(
      map(hasAvailable => {            
        if (hasAvailable) 
          return { inputState: InputStateVisibility.HAS_OTHER_CONTACT_CHANNEL };
        return { inputState: InputStateVisibility.HAS_NO_OTHER_CONTACT_CHANNEL, data: this.determineChannelActionButtons(attendance)};
      })
    );
  }

  private getArchivedAttendanceCtaVisibility(attendance: Attendance): Observable<InputVisibility> {
    return this.attendanceSevice.listContactAttendancesByAttendanceId(attendance.id, attendance.currentCompany.id).pipe(
      map((nextAttendance) => {
        if (!nextAttendance || !nextAttendance.available)
          return { inputState: InputStateVisibility.HAS_NO_OTHER_CONTACT_CHANNEL, data: this.determineChannelActionButtons(attendance)};
        return {inputState: InputStateVisibility.HAS_NEXT_ATTENDANCE, data: {nextAttendanceLink: nextAttendance.available.data.link}};
      }))
  }

  private getLockedEmailAttendanceCtaVisibility(attendance: Attendance){
    return this.hasOtherAvailableChannel(attendance).pipe(map(hasAvailable => {
      return { inputState: InputStateVisibility.LOCKED_EMAIL_ATTENDANCE, data: {lockedEmailAttendance: {showUseAnotherChannel: hasAvailable}}}
    }))
  }

  private getBookingCtaVisibility(attendance: Attendance) {
    return { inputState: InputStateVisibility.LOCKED_BOOKING_ATTENDANCE, data: {
        ...this.determineChannelActionButtons(attendance)
      }
    };
  }

  private getExpediaCtaVisibility(attendance: Attendance) {
    return { inputState: InputStateVisibility.LOCKED_EXPEDIA_ATTENDANCE, data: {
        ...this.determineChannelActionButtons(attendance)
      }
    };
  }

  private getTiktokCtaVisibility(attendance: Attendance) {
    return { inputState: InputStateVisibility.LOCKED_TIKTOK_ATTENDANCE, data: {
        ...this.determineChannelActionButtons(attendance)
      }
    };
  }

  private checkIsMetaAndUnavailable(attendance: Attendance) {
    const isAfter24H = isAfter24Hours(attendance.currentContactChannel.lastMessageReceivedAt!);
    const isAMetaProduct = this.channelUtils.isMetaChannel(attendance.currentContactChannel.channel);
    return isAMetaProduct && isAfter24H || !attendance.currentContactChannel.isAvailable;
  }
  
  private hasOtherAvailableChannel(attendance: Attendance){
    return this.attendanceSevice.getContactChannel(attendance)
      .pipe(map(channels=>
        channels
          .filter(channel=>channel.id !== attendance.currentContactChannel.id)
          .some(c=>c.isAvailable)
        ));
  }

  private checkIfOtherAgentAssigned(attendance: Attendance){
    if (this.isWhatsappWebCoexAttendant(attendance)) return false;
    const user = this.userService.getCurrentUser();
    const isDifferentAgentAssigned = user.id !== Number(attendance.currentAttendant?.userReferenceId);
    return isDifferentAgentAssigned || !attendance.isBotStopped
  }

  // Coexistence: the echo alternated the attendance to the WhatsApp Web system
  // user; the agent may type straight away — the send itself re-alternates to them.
  private isWhatsappWebCoexAttendant(attendance: Attendance): boolean {
    return attendance.currentContactChannel?.channel === AttendanceChannel.WHATSAPP
      && attendance.currentAttendant?.userReferenceId === WHATSAPP_WEB_USER_REFERENCE_ID
      && !!attendance.isBotStopped;
  }

  private determineChannelActionButtons(
    attendance: Attendance
  ): InputStateData {
    const companyHasEmail = this.getCompanies()!.find((c) => c.hasEmail);
    const companyHasWhatsApp = this.getCompanies()!.find((c) => c.hasWhatsapp);
    const companyWithPhone = this.getCompanies()!.find((c) => c.hasPhone);
    const contactHasEmail = attendance?.contact?.email;
    const contactHasPhone = attendance?.contact?.phone;
    // Contacts with a WhatsApp username may have no phone at all: the WhatsApp
    // app can still start a conversation addressing them by the channel's BSUID.
    const contactHasOnlyBsuid =
      !contactHasPhone && this.canStartWhatsappByBsuid(attendance);

    const channelActionButtons: ChannelActionButtons = {
      email: 'not-available',
      whatsapp: 'not-available',
      phone: 'not-available',
    };

    if (contactHasEmail) {
      if (companyHasEmail) channelActionButtons.email = 'send';
      else channelActionButtons.email = 'integrate';
    }

    if (contactHasPhone || contactHasOnlyBsuid) {
      if (companyHasWhatsApp) channelActionButtons.whatsapp = 'send';
      else channelActionButtons.whatsapp = 'integrate';
    }

    if (contactHasPhone && companyWithPhone) {
      channelActionButtons.phone = 'send';
    }

    return { channelActionButtons, whatsappByBsuid: contactHasOnlyBsuid };
  }

  /**
   * TAS-572: "Request phone" (WhatsApp request_contact_info) is a session
   * message, so it is only offered while the input is unlocked (24h window
   * open), and only to contacts known solely by their BSUID (no phone).
   */
  public canRequestContactInfo(
    attendance: Attendance | undefined,
    inputState: InputStateVisibility | undefined
  ): boolean {
    const contactChannel = attendance?.currentContactChannel;
    if (
      !attendance ||
      inputState !== InputStateVisibility.UNLOCKED ||
      contactChannel?.channel !== AttendanceChannel.WHATSAPP ||
      !this.isPhonelessBsuidContact(attendance) ||
      !this.isGupshupWhatsapp(attendance)
    ) {
      return false;
    }

    return this.featureToggleService.companyIdsCanAccessFromCache(
      this.getAttendanceCompaniesIds(attendance),
      FeatureToggles.ENABLE_REQUEST_CONTACT_INFO
    );
  }

  // The attendance list does not carry the channel platform: when it is
  // missing, fall back to the company having Gupshup WhatsApp with a number.
  private isGupshupWhatsapp(attendance: Attendance): boolean {
    const platform = attendance.currentContactChannel?.platform;
    if (platform) {
      return platform === WHATSAPP_GUPSHUP_PLATFORM;
    }

    const companiesIds = this.getAttendanceCompaniesIds(attendance);
    return (this.company.getCompanies() || []).some(
      (company) =>
        companiesIds.includes(company.companyId) && !!company.hasWhatsappCredits
    );
  }

  /** Contact known only by the channel's BSUID (WhatsApp username, no phone). */
  public isPhonelessBsuidContact(attendance: Attendance | undefined): boolean {
    return (
      !!attendance &&
      !attendance.contact?.phone &&
      attendance.currentContactChannel?.channel === AttendanceChannel.WHATSAPP &&
      isBsuid(attendance.currentContactChannel.contactChannelReferenceId)
    );
  }

  private getAttendanceCompaniesIds(attendance: Attendance): string[] {
    const companiesIds = [
      attendance.currentCompany?.id,
      attendance.currentContactChannel?.companyId,
    ].filter((companyId): companyId is string => !!companyId);

    return [...new Set(companiesIds)];
  }

  // Both toggles are required: the visual one releases the flow, the generic-bot
  // one guarantees the BSUID send is honored. Checked on the attendance company
  // and on the channel's company (owner of the WhatsApp number), mirroring the
  // generic-bot company-or-parent check.
  private canStartWhatsappByBsuid(attendance: Attendance): boolean {
    const contactChannel = attendance?.currentContactChannel;
    if (
      contactChannel?.channel !== AttendanceChannel.WHATSAPP ||
      !isBsuid(contactChannel.contactChannelReferenceId)
    ) {
      return false;
    }

    const companiesIds = this.getAttendanceCompaniesIds(attendance);

    return [
      FeatureToggles.WPP_GOD_BSUID_START_CONVERSATION,
      FeatureToggles.ENABLE_GUPSHUP_BSUID_SEND_V1,
    ].every((featureToggle) =>
      this.featureToggleService.companyIdsCanAccessFromCache(
        companiesIds,
        featureToggle
      )
    );
  }

  private getCompanies(): Company[] {
    if (this.userService.isCurrentUserGlobal()) {
      const companyIds = this.filterService.filterState?.companyIds ?? [];

      const companies =
        companyIds
        .map((companyId) => this.company.getCompanies()?.find((c) => c.companyId === companyId) as Company)
        .filter(Boolean);
      return companies;
    } else {
      return this.company.getCompanies() || [];
    }
  }
}
